import "./ctl-menu-item.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display:block;
    font-family: var(--ctl-font-sans);
    color: var(--ctl-color-text);
  }

  .menu{
    display:flex;
    flex-direction: column;
    gap: var(--ctl-space-1);
    outline: none;
  }

  ::slotted(ctl-menu-item){
    width: 100%;
  }

  .sep{
    height: var(--ctl-border-width-thin);
    background: var(--ctl-color-border);
    margin: var(--ctl-space-2) var(--ctl-space-2);
  }
</style>

<div class="menu" id="menu" role="menu" tabindex="0" part="menu">
  <slot></slot>
</div>
`;

export class CtlMenu extends HTMLElement {
  constructor(){
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._menu = this.shadowRoot.getElementById("menu");

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
  }

  connectedCallback(){
    const slot = this.shadowRoot.querySelector("slot");
    slot.addEventListener("slotchange", this._onSlotChange);
    this._menu.addEventListener("keydown", this._onKeyDown);
    this._menu.addEventListener("click", this._onClick);

    // initialize roving tabindex
    queueMicrotask(() => this._initRoving());
  }

  disconnectedCallback(){
    const slot = this.shadowRoot.querySelector("slot");
    slot.removeEventListener("slotchange", this._onSlotChange);
    this._menu.removeEventListener("keydown", this._onKeyDown);
    this._menu.removeEventListener("click", this._onClick);
  }

  _onSlotChange(){
    this._initRoving();
  }

  _items(){
    const slot = this.shadowRoot.querySelector("slot");
    const els = slot.assignedElements({ flatten: true }) || [];
    return els.filter((el) => el.tagName === "CTL-MENU-ITEM");
  }

  _enabledItems(){
    return this._items().filter((it) => !it.disabled);
  }

  _initRoving(){
    const items = this._enabledItems();
    items.forEach((it) => it.tabIndex = -1);
    if (items[0]) items[0].tabIndex = 0;
  }

  focusFirst(){
    const items = this._enabledItems();
    items.forEach((it) => it.tabIndex = -1);
    if (items[0]) { items[0].tabIndex = 0; items[0].focus(); }
  }

  focusLast(){
    const items = this._enabledItems();
    items.forEach((it) => it.tabIndex = -1);
    const last = items[items.length - 1];
    if (last) { last.tabIndex = 0; last.focus(); }
  }

  _move(delta){
    const items = this._enabledItems();
    if (!items.length) return;

    const active = this.getRootNode().activeElement;
    const idx = items.indexOf(active);
    const next = items[(idx < 0 ? 0 : idx + delta + items.length) % items.length];

    items.forEach((it) => it.tabIndex = -1);
    next.tabIndex = 0;
    next.focus();
  }

  _onKeyDown(e){
    if (e.key === "ArrowDown"){ e.preventDefault(); this._move(+1); }
    else if (e.key === "ArrowUp"){ e.preventDefault(); this._move(-1); }
    else if (e.key === "Home"){ e.preventDefault(); this.focusFirst(); }
    else if (e.key === "End"){ e.preventDefault(); this.focusLast(); }
    else if (e.key === "Enter" || e.key === " "){
      const active = this.getRootNode().activeElement;
      if (active && active.tagName === "CTL-MENU-ITEM" && !active.disabled){
        e.preventDefault();
        active.click();
      }
    }
  }

  _onClick(e){
    const path = e.composedPath ? e.composedPath() : [];
    const item = path.find((n) => n && n.tagName === "CTL-MENU-ITEM");
    if (!item) return;
    if (item.disabled) return;

    this.dispatchEvent(new CustomEvent("ctl-change", {
      bubbles: true,
      composed: true,
      detail: { value: item.value, id: item.value, item }
    }));
  }
}

customElements.define("ctl-menu", CtlMenu);
